import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Slide, ToastContainer, toast } from "react-toastify";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../layouts/Default";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../themes/Default/components/Breadcrumps";
import { Link } from "../../themes/Default/components/Breadcrumps/components/Link";
import { Main } from "../../themes/Default/components/Main";
import { Tabs } from "../../themes/Default/components/Tabs";
import { ButtonBack, Button } from "../../themes/Default/components/TopNavigation/components/Button";
import { Form } from "../../themes/Default/components/Form";
import { Row } from "../../themes/Default/components/Form/components/Row";
import { Field } from "../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../themes/Default/components/Form/components/Input";
import { Select } from "../../themes/Default/components/Form/components/Select";
import { Wizard } from "../../themes/Default/components/Wizard";
import { Step } from "../../themes/Default/components/Wizard/Step";
import { prepareSelect } from "../../services/core/Select";
import { employeesManagement } from "../../hooks/api/EmployeesManagement";
import { groupsManagement } from "../../hooks/api/GroupsManagement";
import { Title } from "../../themes/Default/components/Title";
import { Toaster } from "../../themes/Default/components/Toaster";
import DatePicker from "../../themes/Default/components/Form/components/DatePicker/DatePicker";
import { calendarHolidaysManagement } from "../../hooks/api/CalendarHolidaysManagement";
import { format } from "date-fns";

const GroupProfessor = () => {
    const navigate = useNavigate();
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const { groupId } = useParams();
    const [formErrors, setFormErrors] = useState([]);
    const [professors, setProfessors] = useState([]);
    const [coordinators, setCoordinators] = useState([]);
    const [id, setId] = useState();
    const [isStarted, setIsStarted] = useState([]);
    const [groupName, setGroupName] = useState("");
    const [hasTests, setHasTests] = useState(false);
    const [hasProgress, setHasProgress] = useState(false);
    const [hasReport, setHasReport] = useState(false);
    const [hasPlan, setHasPlan] = useState(false);

    const { useEmployeesListMutation } = employeesManagement;
    const { useAddProfessorMutation, useUpdateProfessorMutation, useViewProfessorMutation, useViewGroupMutation, useStartGroupMutation, useCanGroupStartMutation, useAddGroupDatesMutation } = groupsManagement;
    
    const { useCalendarHolidaysListMutation } = calendarHolidaysManagement;
    const [holidayList] = useCalendarHolidaysListMutation();
    const [addGroupDates] = useAddGroupDatesMutation();

    const [professorList] = useEmployeesListMutation();
    const [addGroupProfessor] = useAddProfessorMutation();
    const [viewGroupProfessor] = useViewProfessorMutation();
    const [editGroupProfessor] = useUpdateProfessorMutation();
    const [viewGroup] = useViewGroupMutation();

    const [canGroupStart] = useCanGroupStartMutation();
    const [startGroup, setStartGroup] = useState(true);
    const [groupStart] = useStartGroupMutation();

    const [startDate, setStartDate] = useState([]);
    const [endDate, setEndDate] = useState([]);
    const [selectedWeekdays, setSelectedWeekdays] = useState([]);
    const [classNumber, setClassNumber] = useState();
    const [classStartTime, setClassStartTime] = useState([]);
    const [classEndTime ,setClassEndTime] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [classStartTime2, setClassStartTime2] = useState([]);
    const [selectedWeekdays2, setSelectedWeekdays2] = useState([]);

    const tabs = [
        ...(isStarted ? [
            { title: t('groups.attendance'), onClick: () => gotoAttendanceTab(groupId)  },
            ...(hasPlan ? [
                { title: t('groups.planning'), onClick: () => gotoPlanningTab(groupId)},
            ] : []), 
            ...(hasProgress ? [
                { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
            ] : []), 
            ...(hasTests ? [
                { title: t('groups.progressTest'), onClick: () => gotoProgressTestTab(groupId) }
            ] : []), 
            ...(hasReport ? [
                { title: t('reports.title'),onClick: () => gotoReportTab(groupId)},
            ] : []),
            { title: t('documents'), onClick: () => gotoDocumentsTab(groupId)},
            { title: t('notes'), onClick: () => gotoNotesTab(groupId)},
        ] : []),
        { title: t("settings.title"), link: "/settings/reports", isActive: true },
    ];

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    }

    const gotoReportTab = (groupId) => {
        navigate("/groups/" + groupId + "/reports");
    }

    const gotoProgressTestTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    }

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    }

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    }

    const gotoCalendarStep = () => {
        navigate('/groups/' + groupId + '/calendar/add');
    };

    const gotoStudentsStep = () => {
        navigate("/groups/" + groupId + "/students/add");
    };

    const gotoDetails = (values) => {
        navigate('/groups/edit/' + groupId);
    };

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const getDisabledWeekDays = () => {
        const allDays = [0, 1, 2, 3, 4, 5, 6];
        return allDays.filter(day => !selectedWeekdays.includes(day));
    };

    const getEndDate = async (startDate) => {
        const result = await holidayList();
        const holidays = result?.data?.records || [];
    
        if (!startDate || selectedWeekdays.length === 0 || !classNumber) return "";
    
        const holidayDates = new Set(
            holidays.map((h) => {
                return h.date; 
            })
        );
    
        const selectedDates = [];
        let currentDate = new Date(startDate);
    
        while (selectedDates.length < classNumber) {
            const dayIndex = currentDate.getDay() === 0 ? 6 : currentDate.getDay() - 1;
            const isoDate = currentDate.toISOString().split("T")[0]; 
    
            if (
                selectedWeekdays.includes(dayIndex) &&
                !holidayDates.has(isoDate)
            ) {
                selectedDates.push(new Date(currentDate));
            }
    
            currentDate.setDate(currentDate.getDate() + 1);
        }
    
        const lastDate = selectedDates[selectedDates.length - 1];
        const day = String(lastDate.getDate()).padStart(2, "0");
        const month = String(lastDate.getMonth() + 1).padStart(2, "0");
        const year = lastDate.getFullYear();
    
        return `${day}.${month}.${year}`;
    };

    const onSaved = (values) => {
        if(id){
            const body = { ...values };
            editGroupProfessor({ body, groupId }).then((result) => {
                if (result?.error) {
                    setFormErrors(result?.error?.data?.error?.violations);
                    toast.error(result.error.data.error.responseMessage);
                } else {
                    toast.info(result.data.info.responseMessage); 
                    setRefreshKey(prev => prev + 1); 
                }
            });
        }else{
            addGroupProfessor({body: values, groupId}).then((result) => {
                if (result?.error) {
                    setFormErrors(result?.error?.data?.error?.violations);
                    toast.error(result.error.data.error.responseMessage);
                } else {
                    toast.info(result.data.info.responseMessage); 
                    setRefreshKey(prev => prev + 1);            
                }
            });
        } 
    };

    const generateSelectedDates = async (totalClasses) => {
        if (!startDate || totalClasses <= 0) return [];
    
        const result = await holidayList();
        const holidays = result?.data?.records || [];
        const holidayDates = new Set(holidays.map(h => h.date));
    
        const classes = [];
        let currentDate = new Date(startDate);
    
        const weekdayToggle = {};
    
        while (classes.length < totalClasses) {
            const dayIndex = currentDate.getDay() === 0 ? 6 : currentDate.getDay() - 1;
            const isoDate = currentDate.toISOString().split("T")[0];
    
            if (!holidayDates.has(isoDate)) {
    
                const hasFirst = selectedWeekdays.includes(dayIndex);
                const hasSecond = selectedWeekdays2.includes(dayIndex);
    
                if ((hasFirst || hasSecond) && classes.length < totalClasses) {
    
                    if (hasFirst && hasSecond) {
                        const useFirst = weekdayToggle[dayIndex] ?? true;
    
                        const startTime = useFirst ? classStartTime : classStartTime2;
    
                        classes.push({
                            date: isoDate,
                            timeFrom: startTime,
                            timeTo: calculateEndTime(startTime),
                        });
    
                        weekdayToggle[dayIndex] = !useFirst;
                    } 
                    else if (hasFirst) {
                        classes.push({
                            date: isoDate,
                            timeFrom: classStartTime,
                            timeTo: calculateEndTime(classStartTime),
                        });
                    } 
                    else if (hasSecond) {
                        classes.push({
                            date: isoDate,
                            timeFrom: classStartTime2,
                            timeTo: calculateEndTime(classStartTime2),
                        });
                    }
                }
            }
    
            currentDate.setDate(currentDate.getDate() + 1);
        }
    
        return classes;
    }; 

    const calculateEndTime = (startTime) => {
        if (!startTime || !Number(classEndTime)) return "";
    
        const [h, m] = startTime.split(":").map(Number);
        const date = new Date();
        date.setHours(h, m, 0, 0);
    
        const end = new Date(date.getTime() + Number(classEndTime) * 60000);
    
        return `${end.getHours().toString().padStart(2, "0")}:${end
            .getMinutes()
            .toString()
            .padStart(2, "0")}`;
    };

    const onStartGroup = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("groups.confirmStart")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        (async () => {
                            const selectedDates = await generateSelectedDates(classNumber);
                            
                            if (selectedDates.length === 0) {
                                toast.error("Morate odabrati dane i raspon datuma.");
                                return;
                            }
                        
                            const body = { dates: selectedDates };
                        
                            addGroupDates({ body, groupId }).then((result) => {
                                if (result?.error) {
                                    toast.error(result?.error?.data?.error?.responseMessage);
                                } else {
                                    toast.info(result?.data?.info?.responseMessage);
                                    groupStart({ groupId }).then((result) => {
                                        if (result?.error) {
                                            toast.error(result.error.data.error.responseMessage);
                                        } else {
                                            setStartGroup(false);
                                            setIsStarted(true);
                                            toast.info(result?.data?.info?.responseMessage);
                                        }
                                    });
                                }
                            });
                        })();
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    useEffect(() => {
        professorList({role: 'ROLE_PROFESSOR'}).then((result) => {
            let options = [];
            result?.data?.records.map((professor) => {
                options.push(
                    {
                        id: professor.id,
                        name: professor.firstName + ' ' + professor.lastName
                    }
                );
            })
            setProfessors(prepareSelect(options));
        });
        professorList({role: 'ROLE_PROFESSOR_COORDINATOR'}).then((result) => {
            let options = [];
            result?.data?.records.map((professor) => {
                options.push(
                    {
                        id: professor.id,
                        name: professor.firstName + ' ' + professor.lastName
                    }
                );
            })
            setCoordinators(prepareSelect(options));
        });
        viewGroup(groupId).then((result) => {
            setIsStarted(result?.data?.hasStarted);
            setGroupName(result?.data?.name);
            setHasTests(result?.data?.hasTests);
            setHasReport(result?.data?.hasReport);
            setHasProgress(result?.data?.hasProgress);
            setHasPlan(result?.data?.hasPlan);
            setSelectedWeekdays(Array.isArray(result.data.weekDays) ? result.data.weekDays : []);
            setSelectedWeekdays2(Array.isArray(result.data.weekDays2) ? result.data.weekDays2 : []);
            setClassNumber(result?.data?.classNumber);
            setClassStartTime(result?.data?.classStartTime);
            setClassStartTime2(result?.data?.classStartTime2);

            setClassEndTime(result?.data?.classDuration);

            if (result?.data?.hasStarted === true) {
                setStartGroup(false);
            }else{
                canGroupStart({groupId}).then((result) => {
                    setStartGroup(result?.data?.canStart);
                });
            }
        });
    }, [refreshKey]);

    useEffect(() => {
        viewGroupProfessor({groupId}).then((result) => {
            if(result?.data){
                formRef.current.setValues(result?.data);
                setId(result?.data?.id);
                setStartDate(result?.data?.firstDate);

                if(result?.data?.firstDate){
                    (async () => {
                        const end = await getEndDate(result?.data?.firstDate);
                        setEndDate(end);
                    })();
                }
            }
        });
    }, [selectedWeekdays,classNumber, refreshKey]);


    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/groups" />
                <Button title={t('save')} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
                <Button title={t("groups.startGroup")} icon="fa-regular fa-play" onClick={onStartGroup} isDisabled={!startGroup}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{groupName}</Title>
                <Breadcrumps>
                    <Link title={t('groups.title')} icon="fa-light fa-users"/>
                    <Link title={t('details')} isActive={true}/>
                </Breadcrumps>
                <br/>
                <br/>
                <br/>
                <Wizard>
                    <Step title={t('groups.basicInfo')} isChecked={true} onClick={gotoDetails}/>
                    <Step title={t('groups.chooseStudents')} isChecked={true} onClick={gotoStudentsStep}/>
                    <Step title={t('groups.chooseProfessor')} isActive={true} />
                    <Step title={t('groups.selectDates')} isChecked={true} onClick={isStarted ? gotoCalendarStep : undefined}  />
                </Wizard>
                <br/>
                <br/>
                <br/>
                <Formik
                    innerRef={formRef}
                    initialValues={{ 
                        name: "", 
                        location: null, 
                        color: "", 
                        professor: null,
                        coordinator: null,
                        note: "", 
                        language: "", 
                        program: "", 
                        hasStarted: false, 
                        isBlockClass: false,
                        type: null,
                        firstDate: ""
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange, setFieldValue }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <Select 
                                        name="professor" 
                                        label={t('groups.professor')} 
                                        onChange={handleChange} 
                                        required={true} 
                                        options={professors} 
                                        selectedValue={values.professor} 
                                        error={formErrors?.professor}
                                        searchInDropdown={false}
                                    />
                                </Field>
                                <Field>
                                    <Select 
                                        name="coordinator" 
                                        label={t('groups.coordinator')} 
                                        onChange={handleChange} 
                                        required={true} 
                                        options={coordinators} 
                                        selectedValue={values.coordinator} 
                                        error={formErrors?.coordinator}
                                        searchInDropdown={false}
                                    />
                                </Field>
                            </Row>
                            {/* <Row>
                                <Field>
                                    <Input 
                                        name="classroom" 
                                        label={t('groups.classroom')} 
                                        value={values.classroom} 
                                        onChange={handleChange} 
                                        required={true} 
                                        error={formErrors?.classroom}
                                    />
                                </Field>
                            </Row> */}
                            <Row>
                                <Field>
                                    <DatePicker
                                        name="firstDate"
                                        label={t("groups.startDate")}
                                        value={values.firstDate}
                                        onChange={async (e) => {
                                            const value = e.target.value;
                                            setFieldValue("firstDate", value);
                                        
                                            const end = await getEndDate(value);
                                            setEndDate(end);
                                        }}                                  
                                        error={formErrors?.firstDate}
                                        disabledWeekdays={getDisabledWeekDays()}
                                        disablePast={true}
                                    />
                                </Field>
                                <Field>
                                    <Input 
                                        label={t('groups.endDate')} 
                                        value={endDate} 
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default GroupProfessor;
